import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Save,
  DollarSign,
  // Edit2,
  Plus,
  Trash,
} from "lucide-react";
import { settingsService, pricingTierService } from "@/lib/database";
import type { PricingTier } from "@/lib/supabase";
import { List, arrayMove } from "react-movable";

export default function AdminSettings() {
  const [settings, setSettings] = useState({
    seaRate: 20.0 as number | undefined,
    deliveryFeeMin: 10.0 as number | undefined,
    deliveryFeeMax: 25.0 as number | undefined,
    pickupFeeMin: 5.0 as number | undefined,
    pickupFeeMax: 15.0 as number | undefined,
    expressFee: 4.75 as number | undefined,
  });
  const [pricingTiers, setPricingTiers] = useState<PricingTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingSeaRate, setSavingSeaRate] = useState(false);
  const [savingExpressFee, setSavingExpressFee] = useState(false);
  const [isSavingPricingTiers, setIsSavingPricingTiers] = useState(false);
  // const [editingTierId, setEditingTierId] = useState<string | null>(null);
  // const [newTierName, setNewTierName] = useState<string>('');
  const [tiersToDelete, setTiersToDelete] = useState<PricingTier["id"][]>([]);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);

      // Load general settings
      const generalSettings = await settingsService.getPricingSettings();
      setSettings(generalSettings);

      // Load pricing tiers
      const tiers = await pricingTierService.getAll();
      setPricingTiers(tiers);
    } catch (error) {
      console.error("Error loading settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      await Promise.all([
        settingsService.update(
          "delivery_fee_min",
          settings.deliveryFeeMin!.toString()
        ),
        settingsService.update(
          "delivery_fee_max",
          settings.deliveryFeeMax!.toString()
        ),
        settingsService.update(
          "pickup_fee_min",
          settings.pickupFeeMin!.toString()
        ),
        settingsService.update(
          "pickup_fee_max",
          settings.pickupFeeMax!.toString()
        ),
      ]);

      alert("Settings saved successfully!");
    } catch (error) {
      console.error("Error saving settings:", error);
      alert("Error saving settings. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleSeaRateSave = async () => {
    try {
      setSavingSeaRate(true);

      await settingsService.update(
        "sea_rate_per_cft",
        settings.seaRate!.toString()
      );

      alert("Sea rate saved successfully!");
    } catch (error) {
      console.error("Error saving sea rate:", error);
      alert("Error saving sea rate. Please try again.");
    } finally {
      setSavingSeaRate(false);
    }
  };

  const handleExpressFeeSave = async () => {
    try {
      setSavingExpressFee(true);

      await settingsService.update(
        "express_shipping_fee",
        settings.expressFee!.toString()
      );

      alert("Express fee saved successfully!");
    } catch (error) {
      console.error("Error saving express air fee:", error);
      alert("Error saving express air fee. Please try again.");
    } finally {
      setSavingExpressFee(false);
    }
  };

  const handleTierUpdate = async (
    tierId: string,
    field: keyof PricingTier,
    value: number | null
  ) => {
    try {
      // Update local state
      setPricingTiers((prev) =>
        prev.map((tier) =>
          tier.id === tierId ? { ...tier, [field]: value } : tier
        )
      );
    } catch (error) {
      console.error("Error updating pricing tier:", error);
      alert("Error updating pricing tier. Please try again.");
    }
  };

  const handlePricingTiersSave = async () => {
    try {
      setIsSavingPricingTiers(true);

      // Fix tier orders based on current positions
      const reorderedTiers = pricingTiers.map((tier, index) => ({
        ...tier,
        tier_order: index + 1,
        tier_name: `${tier.min_weight}-${tier.max_weight}`,
      }));

      await pricingTierService.deleteMultiple(tiersToDelete);
      const updatedTiers = await pricingTierService.updateMultiple(
        reorderedTiers
      );

      setPricingTiers(updatedTiers);
      setTiersToDelete([]);

      alert("Pricing tiers saved successfully!");
    } catch (error) {
      console.error("Error saving pricing tiers:", error);
      alert("Error saving pricing tiers. Please try again.");
    } finally {
      setIsSavingPricingTiers(false);
    }
  };

  const handleTierReorder = ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => {
    const reorderedTiers = arrayMove(pricingTiers, oldIndex, newIndex);

    setPricingTiers(
      reorderedTiers.map((tier, index) => ({
        ...tier,
        tier_order: index + 1, // Update tier_order based on new position
      }))
    );
  };

  // const handleToggleEditTierName = (tierId: string) => {
  //   setNewTierName(pricingTiers.find(tier => tier.id === tierId)?.tier_name || '');
  //   setEditingTierId(tierId);
  // }

  // const handleSaveTierName = async (tierId: string) => {
  //   setPricingTiers(prev => prev.map(tier =>
  //     tier.id === tierId ? { ...tier, tier_name: newTierName } : tier
  //   ));

  //   setEditingTierId(null);
  //   setNewTierName('');
  // }

  const handleAddNewTier = (afterTierId?: string) => {
    if (afterTierId) {
      const afterTierIdx = pricingTiers.findIndex(
        (tier) => tier.id === afterTierId
      );

      const newTiersList = [...pricingTiers];

      newTiersList.splice(afterTierIdx + 1, 0, {
        id: `new-${Date.now()}`, // Temporary ID for new tier
        tier_name: "New Tier",
        min_weight: 0,
        max_weight: 0,
        rate: 0,
        is_active: true,
        tier_order: 1, // Order will be fixed upon reordering or submission
        created_at: `new-${new Date().toISOString()}`,
      });

      setPricingTiers(newTiersList);
    } else {
      // If no afterTierId is provided, add to the beginning
      setPricingTiers((prev) => [
        {
          id: `new-${Date.now()}`, // Temporary ID for new tier
          tier_name: "New Tier",
          min_weight: 0,
          max_weight: 0,
          rate: 0,
          is_active: true,
          tier_order: 1, // Order will be fixed upon reordering or submission
          created_at: `new-${new Date().toISOString()}`,
        },
        ...prev,
      ]);
    }
  };

  const handleDeleteTier = (tierId: string) => {
    const tierIdx = pricingTiers.findIndex((tier) => tier.id === tierId);

    // Only for existing tiers
    if (!pricingTiers[tierIdx].id.startsWith("new")) {
      setTiersToDelete((prev) => [...prev, tierId]);
    }

    const newTiersList = [...pricingTiers];

    newTiersList.splice(tierIdx, 1);

    setPricingTiers(newTiersList);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
          System Settings
        </h1>
        <p className="text-gray-600">
          Configure pricing and system preferences
        </p>
      </div>

      {/* Pricing Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center text-xl md:text-2xl">
            <DollarSign className="h-5 w-5 mr-2" />
            Pickup and Delivery Fees
          </CardTitle>
          <CardDescription>
            Configure pickup and delivery fee ranges
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-8">
          {/* Pickup Fees */}
          <div>
            <h4 className="font-medium text-gray-900 mb-4 text-lg md:text-xl">
              Pickup Fees
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pickupFeeMin">Min Pickup Fee</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                    $
                  </span>
                  <Input
                    id="pickupFeeMin"
                    type="text"
                    step="0.01"
                    value={settings.pickupFeeMin}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        pickupFeeMin: parseFloat(e.target.value) || 0,
                      }))
                    }
                    className="pl-8"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pickupFeeMax">Max Pickup Fee</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                    $
                  </span>
                  <Input
                    id="pickupFeeMax"
                    type="text"
                    step="0.01"
                    value={settings.pickupFeeMax}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        pickupFeeMax: parseFloat(e.target.value) || 0,
                      }))
                    }
                    className="pl-8"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Delivery Fees */}
          <div>
            <h4 className="font-medium text-gray-900 mb-4 text-lg md:text-xl">
              Delivery Fees
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="deliveryFeeMin">Min Delivery Fee</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                    $
                  </span>
                  <Input
                    id="deliveryFeeMin"
                    type="text"
                    step="0.01"
                    value={settings.deliveryFeeMin}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        deliveryFeeMin: parseFloat(e.target.value) || 0,
                      }))
                    }
                    className="pl-8"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="deliveryFeeMax">Max Delivery Fee</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                    $
                  </span>
                  <Input
                    id="deliveryFeeMax"
                    type="text"
                    step="0.01"
                    value={settings.deliveryFeeMax}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        deliveryFeeMax: parseFloat(e.target.value) || 0,
                      }))
                    }
                    className="pl-8"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#254164] hover:bg-[#1a2d47]"
            >
              {saving ? (
                <>
                  <Save className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Pricing
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center text-xl md:text-2xl">
            <DollarSign className="h-5 w-5 mr-2" />
            Sea Shipping Rate
          </CardTitle>
          <CardDescription>Configure Sea Shipping Rate</CardDescription>
        </CardHeader>

        <CardContent className="space-y-8">
          {/* Sea Rate */}
          <div>
            <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label htmlFor="seaRate">
                  Sea Shipping Rate (per cubic foot)
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                    $
                  </span>
                  <Input
                    id="seaRate"
                    type="number"
                    value={settings.seaRate || ""}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        seaRate:
                          e.target.value === ""
                            ? undefined
                            : parseFloat(e.target.value),
                      }))
                    }
                    className="pl-8"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleSeaRateSave}
              disabled={savingSeaRate}
              className="bg-[#254164] hover:bg-[#1a2d47]"
            >
              {savingSeaRate ? (
                <>
                  <Save className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Sea Shipping Rate
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Express Air Shipping Fee */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center text-xl md:text-2xl">
            <DollarSign className="h-5 w-5 mr-2" />
            Express Air Shipping Fee
          </CardTitle>
          <CardDescription>Configure Express Air Shipping Fee</CardDescription>
        </CardHeader>

        <CardContent className="space-y-8">
          <div>
            <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label htmlFor="expressFee">Express Air Shipping Fee</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                    $
                  </span>
                  <Input
                    id="expressFee"
                    type="number"
                    value={settings.expressFee || ""}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        expressFee:
                          e.target.value === ""
                            ? undefined
                            : parseFloat(e.target.value),
                      }))
                    }
                    className="pl-8"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleExpressFeeSave}
              disabled={savingExpressFee}
              className="bg-[#254164] hover:bg-[#1a2d47]"
            >
              {savingExpressFee ? (
                <>
                  <Save className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Express Air Shipping Rate
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Express Pricing Tiers */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center text-xl md:text-2xl">
            <DollarSign className="h-5 w-5 mr-2" />
            Air Shipping Rates
          </CardTitle>
          <CardDescription>
            Configure air shipping rates based on weight ranges
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-8">
          <div className="flex flex-col items-center space-y-2">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => handleAddNewTier()}
              className="bg-[#254164] hover:bg-[#1a2d47] w-5 h-5"
            >
              <Plus className="h-3 w-3 text-white" />
            </Button>

            <List
              values={pricingTiers}
              onChange={handleTierReorder}
              renderList={({ children, props }) => (
                <div className="space-y-2 w-full" {...props}>
                  {children}
                </div>
              )}
              renderItem={({ value: tier, props: { key, ...restProps } }) => (
                <div
                  key={key}
                  className="flex flex-col items-center justify-center space-y-2"
                  {...restProps}
                >
                  <div className="relative flex justify-between flex-col md:flex-row gap-4 p-4 bg-gray-50 shadow-sm border border-gray-200 rounded-lg w-full">
                    <div className="absolute right-3 top-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteTier(tier.id)}
                      >
                        <Trash className="h-3 w-3 text-destructive" />
                      </Button>
                    </div>

                    <div className="space-y-2">
                      {/* {editingTierId === tier.id ? (
                          <form onSubmit={(e) => { e.preventDefault(); handleSaveTierName(tier.id); }}>
                            <div className="flex items-center space-x-2">
                              <Input
                                id={`air-tier-name-${tier.id}`}
                                type="text"
                                value={newTierName ?? ''}
                                onChange={(e) => setNewTierName(e.target.value)}
                                className="h-8 w-64"
                              />

                              <Button type="button" variant="ghost" size="sm" onClick={() => handleSaveTierName(tier.id)}>
                                <Save className="h-3 w-3" />
                              </Button>
                            </div>
                          </form>
                        ) :
                          (
                            <div className="flex items-center space-x-2">
                              <Label className="text-sm font-medium">{tier.tier_name}</Label>

                              <Button variant="ghost" size="sm" onClick={() => handleToggleEditTierName(tier.id)}>
                                <Edit2 className="h-3 w-3 text-gray-500" />
                              </Button>
                            </div>
                          )} */}

                      <div className="flex items-center space-x-2">
                        <Label className="text-sm font-medium">
                          Tier {!isNaN(key!) ? key! + 1 : ""}
                        </Label>
                      </div>

                      <div className="flex space-x-2 items-center">
                        <div className="flex flex-col space-y-2">
                          <div className="relative">
                            <Input
                              id={`air-min-weight-${tier.id}`}
                              type="number"
                              value={tier.min_weight ?? ""}
                              onChange={(e) =>
                                handleTierUpdate(
                                  tier.id,
                                  "min_weight",
                                  e.target.value === ""
                                    ? null
                                    : parseFloat(e.target.value)
                                )
                              }
                              className="w-[80px]"
                            />

                            <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                              lb
                            </span>
                          </div>

                          <Label
                            className="text-xs"
                            htmlFor={`air-min-weight-${tier.id}`}
                          >
                            Min Weight
                          </Label>
                        </div>

                        <span className="text-gray-400">-</span>

                        <div className="flex flex-col space-y-2">
                          <div className="relative">
                            <Input
                              id={`air-max-weight-${tier.id}`}
                              type="number"
                              value={tier.max_weight ?? ""}
                              onChange={(e) =>
                                handleTierUpdate(
                                  tier.id,
                                  "max_weight",
                                  e.target.value === ""
                                    ? null
                                    : parseFloat(e.target.value)
                                )
                              }
                              className="w-[80px]"
                            />

                            <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                              lb
                            </span>
                          </div>

                          <Label
                            className="text-xs"
                            htmlFor={`air-max-weight-${tier.id}`}
                          >
                            Max Weight
                          </Label>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`air-rate-${tier.id}`}>Rate ($/lb)</Label>

                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                          $
                        </span>
                        <Input
                          id={`air-rate-${tier.id}`}
                          type="number"
                          value={tier.rate ?? ""}
                          onChange={(e) =>
                            handleTierUpdate(
                              tier.id,
                              "rate",
                              e.target.value === ""
                                ? null
                                : parseFloat(e.target.value)
                            )
                          }
                          className="pl-8"
                        />
                      </div>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleAddNewTier(tier.id)}
                    className="bg-[#254164] hover:bg-[#1a2d47] w-5 h-5"
                  >
                    <Plus className="h-3 w-3 text-white" />
                  </Button>
                </div>
              )}
            />
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handlePricingTiersSave}
              disabled={isSavingPricingTiers}
              className="bg-[#254164] hover:bg-[#1a2d47]"
            >
              {isSavingPricingTiers ? (
                <>
                  <Save className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Air Shipping Rates
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
